import { useCallback, useEffect, useRef, useState } from 'react';

type UseEarlierMessagesButtonProps = {
  earlierMessagesAvailable?: boolean;
};

export const useEarlierMessagesButton = ({ earlierMessagesAvailable = false }: UseEarlierMessagesButtonProps) => {
  const [showEarlierMessagesButton, setShowEarlierMessagesButton] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedNodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && earlierMessagesAvailable) {
          setShowEarlierMessagesButton(true);
        } else {
          setShowEarlierMessagesButton(false);
        }
      },
      { threshold: 0.01 }
    );

    observerRef.current = observer;

    const node = observedNodeRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [earlierMessagesAvailable]);

  const setOldestMessageRef = useCallback<(node: HTMLDivElement | null) => void>((node) => {
    observedNodeRef.current = node;
    const observer = observerRef.current;
    if (!observer) {
      return;
    }
    observer.disconnect();
    if (node) {
      observer.observe(node);
    }
  }, []);

  return {
    showEarlierMessagesButton,
    setShowEarlierMessagesButton,
    setOldestMessageRef,
  };
};
